import React from "react";
import { Player, SavedTournament } from "../types.ts";
import {
  ResultMatrix,
  calculatePoints,
  calculateRankings,
  combineRounds,
  formatResult,
  formatTime,
  generateTableColors,
} from "../utils/tournament-calc.ts";
import { formatArchiveValue, getArchiveColumns } from "../utils/ranking.ts";

interface TournamentDetailModalProps {
  tournament: SavedTournament;
  /** Všichni hráči – slouží jako záloha pro starší záznamy bez jmen a časů. */
  allPlayers: Player[];
  onClose: () => void;
  /**
   * Smazání turnaje z archivu. Když se nepředá, tlačítko se nezobrazí –
   * veřejná stránka tak zůstává jen ke čtení.
   */
  onDelete?: () => void;
  /** Probíhá mazání – tlačítko je zamčené. */
  deleting?: boolean;
}

const RotatedHeaderCell = ({
  children,
  wide,
}: {
  children: React.ReactNode;
  /** Dvoukolový turnaj potřebuje v buňce místo na dva výsledky. */
  wide?: boolean;
}) => (
  <th
    className={`px-1 py-1 text-center font-medium text-gray-500 tracking-wider border ${
      wide ? "max-w-[52px] min-w-[52px]" : "max-w-[22px] min-w-[22px]"
    }`}
  >
    <div className="h-32 flex items-center justify-center">
      <div className="transform -rotate-90 whitespace-nowrap text-center">
        {children}
      </div>
    </div>
  </th>
);

const parseMatrix = (raw: unknown): ResultMatrix => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as ResultMatrix;
  try {
    const parsed = JSON.parse(String(raw));
    return Array.isArray(parsed) ? (parsed as ResultMatrix) : [];
  } catch (e) {
    console.error("Nepodařilo se načíst výsledky turnaje:", e);
    return [];
  }
};

const TournamentDetailModal: React.FC<TournamentDetailModalProps> = ({
  tournament,
  allPlayers,
  onClose,
  onDelete,
  deleting = false,
}) => {
  const matrix = parseMatrix(tournament.results);
  // Dvoukolový turnaj má odvety v samostatné matici
  const matrix2 = tournament.doubleRound
    ? parseMatrix(tournament.results2)
    : null;
  const isDoubleRound = Boolean(matrix2 && matrix2.length > 0);
  const playerIds = tournament.player || [];
  const playerCount =
    tournament.playerCount || playerIds.length || matrix.length;

  const findPlayer = (index: number): Player | undefined => {
    const id = playerIds[index];
    if (!id) return undefined;
    return allPlayers.find((p) => p.id === id);
  };

  // Jméno a čas se berou přednostně z uloženého turnaje – jde o stav,
  // ve kterém se turnaj odehrál. Starší záznamy je dohledají u hráče.
  const getName = (index: number): string => {
    const saved = tournament.playerNames?.[index];
    if (saved) return saved;
    return findPlayer(index)?.player_name || `${index + 1}`;
  };

  const getTime = (index: number): number | null => {
    const saved = tournament.times?.[index];
    if (saved !== undefined && saved !== null) return saved;
    return findPlayer(index)?.player_handicap ?? null;
  };

  // Starší turnaje nové časy uložené nemají – sloupec se pak nezobrazuje
  const hasNewTimes = Boolean(
    tournament.newTimes && tournament.newTimes.some((t) => t !== null)
  );

  // Uložené hodnoty mají přednost, dopočet slouží pro starší záznamy.
  const scoreMatrix = combineRounds(matrix, matrix2, playerCount);

  const points =
    tournament.points && tournament.points.length
      ? tournament.points
      : calculatePoints(scoreMatrix, playerCount);
  // Pomocná hodnocení podle režimu, se kterým byl turnaj uzavřen
  const archiveColumns = getArchiveColumns(
    tournament,
    matrix2 && matrix2.length ? [matrix, matrix2] : [matrix],
    playerCount
  );
  const rankings: { [key: string]: number } =
    tournament.rankings && Object.keys(tournament.rankings).length
      ? tournament.rankings
      : (calculateRankings(scoreMatrix, playerCount) as {
          [key: string]: number;
        });

  const colors = generateTableColors(playerCount);

  return (
    <div className="fixed inset-0 z-20 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 py-8 text-center">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block w-full max-w-6xl p-6 my-4 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg relative">
          <div className="flex justify-between items-center pb-4">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Turnaj {tournament.date}
              </h3>
              <p className="text-sm text-gray-500">
                Archivovaný turnaj – výsledky už nelze měnit.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Zavřít</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {matrix.length === 0 ? (
            <p className="text-gray-500 py-6 text-center">
              Pro tento turnaj nejsou uložené výsledky.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border text-sm select-none">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border w-10">
                      #
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border w-10">
                      Čas
                    </th>
                    {hasNewTimes && (
                      <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border w-10">
                        Nový čas
                      </th>
                    )}
                    <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border w-32">
                      Hráč
                    </th>
                    {Array(playerCount)
                      .fill(0)
                      .map((_, index) => (
                        <RotatedHeaderCell key={index} wide={isDoubleRound}>
                          {getName(index)}
                        </RotatedHeaderCell>
                      ))}
                    <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase tracking-wider border">
                      Body
                    </th>
                    {archiveColumns.map((column) => (
                      <th
                        key={column.key}
                        title={column.full}
                        className="px-2 py-2 text-center font-medium text-gray-500 uppercase tracking-wider border"
                      >
                        {column.short}
                      </th>
                    ))}
                    <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase tracking-wider border">
                      Pořadí
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array(playerCount)
                    .fill(0)
                    .map((_, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={
                          rankings[rowIndex] === 1
                            ? "row-gold"
                            : rankings[rowIndex] === 2
                            ? "row-silver"
                            : rankings[rowIndex] === 3
                            ? "row-bronze"
                            : ""
                        }
                      >
                        <td className="px-2 py-2 whitespace-nowrap border text-center">
                          {rowIndex + 1}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap border text-center">
                          {formatTime(getTime(rowIndex))}
                        </td>
                        {hasNewTimes && (
                          <td className="px-2 py-2 whitespace-nowrap border text-center">
                            {formatTime(tournament.newTimes?.[rowIndex])}
                          </td>
                        )}
                        <td className="px-4 py-2 whitespace-nowrap border">
                          {getName(rowIndex)}
                        </td>
                        {Array(playerCount)
                          .fill(0)
                          .map((_, colIndex) => (
                            <td
                              key={colIndex}
                              className={`px-2 py-2 whitespace-nowrap text-center border ${colors[rowIndex][colIndex]}`}
                            >
                              <span className="font-extrabold">
                                {isDoubleRound && rowIndex !== colIndex ? (
                                  <>
                                    {formatResult(matrix[rowIndex]?.[colIndex])}
                                    <span className="text-gray-300 font-normal">
                                      {" | "}
                                    </span>
                                    {formatResult(
                                      matrix2?.[rowIndex]?.[colIndex]
                                    )}
                                  </>
                                ) : (
                                  formatResult(matrix[rowIndex]?.[colIndex])
                                )}
                              </span>
                            </td>
                          ))}
                        <td className="px-2 py-2 whitespace-nowrap border text-center font-bold">
                          {points[rowIndex] !== undefined
                            ? points[rowIndex]
                            : ""}
                        </td>
                        {archiveColumns.map((column) => (
                          <td
                            key={column.key}
                            className="px-2 py-2 whitespace-nowrap border text-center"
                          >
                            {formatArchiveValue(column, rowIndex)}
                          </td>
                        ))}
                        <td
                          className={`px-2 py-2 whitespace-nowrap border text-center ${
                            rankings[rowIndex] === 1 ? "gold-cup" : ""
                          }`}
                        >
                          {rankings[rowIndex] !== undefined
                            ? rankings[rowIndex]
                            : ""}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex justify-between items-center gap-4">
            {onDelete ? (
              <button
                onClick={onDelete}
                disabled={deleting}
                className={`px-4 py-2 rounded-md focus:outline-none ${
                  deleting
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                {deleting ? "Mažu…" : "Smazat turnaj"}
              </button>
            ) : (
              <span />
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 focus:outline-none"
            >
              Zavřít
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentDetailModal;
